import { XMarkIcon } from "@heroicons/react/24/solid";
import { useEffect, useMemo, useState } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { SearchInput } from "@/core/components/search-input";
import { Button } from "@/core/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverClose,
} from "@/core/components/ui/popover";
import { Stack } from "@/core/components/ui/stack";
import {
  Tabs,
  TabsTrigger,
  TabsListWithIndicator,
  TabsContent,
} from "@/core/components/ui/tabs";
import { Typography } from "@/core/components/ui/typography";
import {
  HeaderStack,
  MainCard,
  MainStack,
} from "@/business-settings/common/business-settings-page";
import { PageHeader } from "@/business-settings/common/page-header";
import { SIDEBAR_MODES } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import type { BusinessSettingsSidebarSection } from "@/business-settings/context/business-settings-sidebar-context";
import { useBusiness } from "@/core/business";
import { useDebounce } from "@/core/hooks";
import type { CustomerWithReferralsAndPayouts } from "@/customer/customer";
import { useGetCustomersV2 } from "@/customer/query";
import AddReferralModal from "@/referrals/common/add-referral-modal";
import { Advocates } from "./advocates";
import { CUSTOMER_TABS, CUSTOMER_TABS_LABELS } from "./constants";
import { Leads } from "./leads";

const Customers = () => {
  const { businessId, tab } = useParams();
  const [searchParams] = useSearchParams();
  const { isSpiffOnly } = useBusiness();
  const navigate = useNavigate();
  const {
    updateSidebar,
    state: { isOpen, selectedItemId },
  } = useBusinessSettingsSidebar();
  const [addReferralModalOpen, setAddReferralModalOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 300);

  const defaultTab = CUSTOMER_TABS.leads;
  const activeTab = tab ?? defaultTab;
  const activeCustomerType =
    activeTab === CUSTOMER_TABS.advocates
      ? CUSTOMER_TABS.advocates
      : CUSTOMER_TABS.leads;
  const sortByParam = searchParams.get("sort");
  const sortDirectionParam =
    searchParams.get("desc") === "true" ? "desc" : "asc";
  const sortBy =
    sortByParam === "name" ||
    sortByParam === "created_at" ||
    sortByParam === "referrals_count" ||
    sortByParam === "payouts_total"
      ? sortByParam
      : "created_at";
  const sortDirection = sortByParam ? sortDirectionParam : "desc";

  const customersQuery = useGetCustomersV2<CustomerWithReferralsAndPayouts>(
    businessId,
    {
      customerType: activeCustomerType,
      search: debouncedSearchValue,
      sortBy,
      sortDirection,
      withPayouts: true,
      withReferrals: true,
    },
  );

  useEffect(() => {
    if (!tab) {
      navigate(`/business-settings/${businessId}/customers/${defaultTab}`);
    }
  }, [tab, navigate, businessId, defaultTab]);

  const customers = useMemo(
    () => customersQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [customersQuery.data],
  );

  const counts = customersQuery.data?.pages[0]?.counts;
  const leadCount = counts?.leads ?? 0;
  const advocateCount = counts?.advocates ?? 0;
  const isLoading = customersQuery.isLoading;
  const hasSearch = !!debouncedSearchValue.trim();
  const crossTabCount =
    activeTab === CUSTOMER_TABS.leads ? advocateCount : leadCount;
  const crossTabLabel =
    activeTab === CUSTOMER_TABS.leads
      ? CUSTOMER_TABS_LABELS[CUSTOMER_TABS.advocates]
      : CUSTOMER_TABS_LABELS[CUSTOMER_TABS.leads];
  const crossTabValue =
    activeTab === CUSTOMER_TABS.leads
      ? CUSTOMER_TABS.advocates
      : CUSTOMER_TABS.leads;
  const loadMoreLabel =
    activeTab === CUSTOMER_TABS.leads
      ? "Load more leads"
      : "Load more advocates";
  const scrollResetKey = `${activeCustomerType}:${debouncedSearchValue.trim()}:${sortBy}:${sortDirection}`;

  const handleNewCustomer = () => {
    if (activeTab === CUSTOMER_TABS.leads) {
      setAddReferralModalOpen(true);
    } else {
      updateSidebar({
        section: activeTab as BusinessSettingsSidebarSection,
        mode: SIDEBAR_MODES.new,
        selectedItemId: undefined,
        isOpen: true,
      });
    }
  };

  const handleTabChange = (tab: string) => {
    updateSidebar({
      selectedItemId: undefined,
      mode: SIDEBAR_MODES.view,
      section: activeTab as BusinessSettingsSidebarSection,
      isOpen: false,
    });
    navigate(`/business-settings/${businessId}/customers/${tab}`);
  };

  const bottomContent = customersQuery.hasNextPage ? (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => customersQuery.fetchNextPage()}
      loading={customersQuery.isFetchingNextPage}
    >
      {loadMoreLabel}
    </Button>
  ) : null;

  if (isSpiffOnly) {
    return <Navigate to={`/business-settings/${businessId}/`} replace />;
  }

  return (
    <>
      <Stack className="mr-5" gap="sm" fullWidth>
        <HeaderStack>
          <PageHeader>Customers</PageHeader>
          <Stack direction="row" gap="sm" className="items-center">
            <SearchInput
              placeholder="Search customers"
              value={searchValue}
              onChange={setSearchValue}
              className="w-[300px] bg-card"
            />
            <Button variant="secondary" size="sm" onClick={handleNewCustomer}>
              {activeTab === CUSTOMER_TABS.leads
                ? "Add Referral"
                : "New Advocate"}
            </Button>
          </Stack>
        </HeaderStack>
        <MainCard>
          <MainStack>
            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
              className="relative flex w-full flex-1 flex-col overflow-hidden"
            >
              <TabsListWithIndicator
                className="w-full flex-grow-0 justify-start"
                containerClassName="mr-5"
              >
                <TabsTrigger
                  value={CUSTOMER_TABS.leads}
                  count={leadCount}
                  isCountLoading={isLoading}
                >
                  {CUSTOMER_TABS_LABELS[CUSTOMER_TABS.leads]}
                </TabsTrigger>
                <TabsTrigger
                  value={CUSTOMER_TABS.advocates}
                  count={advocateCount}
                  isCountLoading={isLoading}
                >
                  {CUSTOMER_TABS_LABELS[CUSTOMER_TABS.advocates]}
                </TabsTrigger>
              </TabsListWithIndicator>
              <Popover>
                <PopoverTrigger className="absolute right-5 top-5">
                  <Typography
                    variant="bodyW1"
                    textColor="interactive"
                    className="cursor-pointer underline"
                  >
                    What are leads and advocates?
                  </Typography>
                </PopoverTrigger>
                <PopoverContent className="relative bg-primary-text p-6">
                  <PopoverClose className="absolute right-2 top-2">
                    <XMarkIcon
                      className="h-6 w-6 cursor-pointer"
                      color="white"
                    />
                  </PopoverClose>
                  <Typography
                    variant="bodyW2"
                    textColor="primaryInverse"
                    className="mb-4"
                  >
                    A <b>lead</b> is a person looking for service. Their
                    information was sent in by an advocate of your business.
                    This is basically a referral before they (hopefully) turn
                    into an advocate after being serviced.
                  </Typography>
                  <Typography variant="bodyW2" textColor="primaryInverse">
                    An <b>advocate</b> is a person who texted <b>Referral</b> to
                    your business hotline and was given information on how to
                    send in referrals. If they used a specific code to sign up,
                    any referrals they send in are attributed to the business
                    user that signed them up.
                  </Typography>
                </PopoverContent>
              </Popover>
              <TabsContent
                value={CUSTOMER_TABS.leads}
                className="mt-0 flex-1 overflow-hidden pt-4"
              >
                <Leads
                  customers={customers}
                  bottomContent={bottomContent}
                  isLoading={isLoading}
                  manualSorting
                  selectedCustomerId={isOpen ? selectedItemId : undefined}
                  scrollResetKey={scrollResetKey}
                />
              </TabsContent>
              <TabsContent
                value={CUSTOMER_TABS.advocates}
                className="mt-0 flex-1 overflow-hidden pt-4"
              >
                <Advocates
                  customers={customers}
                  bottomContent={bottomContent}
                  isLoading={isLoading}
                  manualSorting
                  selectedCustomerId={isOpen ? selectedItemId : undefined}
                  scrollResetKey={scrollResetKey}
                />
              </TabsContent>
              {hasSearch && crossTabCount > 0 && (
                <Stack className="p-4 pb-8">
                  <Typography
                    variant="bodyW2"
                    textColor="interactive"
                    className="cursor-pointer underline"
                    onClick={() => handleTabChange(crossTabValue)}
                  >
                    {crossTabCount} result{crossTabCount !== 1 ? "s" : ""} in{" "}
                    {crossTabLabel}
                  </Typography>
                </Stack>
              )}
            </Tabs>
          </MainStack>
        </MainCard>
      </Stack>
      <AddReferralModal
        businessId={businessId}
        open={addReferralModalOpen}
        onOpenChange={setAddReferralModalOpen}
        onSuccess={(data) => {
          updateSidebar({
            section: activeTab as BusinessSettingsSidebarSection,
            mode: SIDEBAR_MODES.view,
            selectedItemId: data.potential_customer?.id,
            isOpen: true,
          });
        }}
      />
    </>
  );
};

export default Customers;
